import React, { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'lodash';
import { DateModel } from '../../types';
import { isDateFilled } from '../../services';
import {
  BaseFieldProps,
  FloatingLabelWrapper,
  FloatingLabelState,
  Text,
} from '@tradingblock/components';
import { formatNaturalDateInput, parseNaturalDateInput, toNaturalDateInputValue } from './dateInput';

interface DateFieldProps extends BaseFieldProps { }

export const DateField: React.FC<DateFieldProps> = ({
  form,
  id,
  disabled,
  defaultLabelId,
  controlledLabel,
  onLabelStateChange,
  errorAfterSubmit,
}) => {
  const { values, touched, errors, setFieldValue, setFieldTouched } = form;
  const value = useMemo(() => _.get(values, id) as DateModel | undefined, [values, id]);
  const error = _.get(touched, id) ? _.pickBy(errors, (err, key) => err && _.startsWith(key, id)) : undefined;
  const isDateOfBirth = _.endsWith(id, 'dateOfBirth');
  const [floatingLabelState, setFloatingLabelState] = useState<FloatingLabelState>({ isFilled: isDateFilled(value) });
  const updateFloatingLabelState = useCallback(
    (state: FloatingLabelState) => {
      setFloatingLabelState(state);
      if (onLabelStateChange) {
        onLabelStateChange(state);
      }
    },
    [onLabelStateChange]
  );

  // showError will only show errors when field is touched or after submit if errorAfterSubmit is true
  const showError = errorAfterSubmit ? !!form.submitCount : !!_.get(touched, id);

  useEffect(() => {
    if (!controlledLabel) {
      updateFloatingLabelState({ isFilled: isDateFilled(value), hasFocus: floatingLabelState.hasFocus });
    }
  }, [value, controlledLabel, floatingLabelState.hasFocus, updateFloatingLabelState]);

  const naturalDateInputValue = useMemo(() => toNaturalDateInputValue(value), [value]);

  /** While focused, keep the keystrokes on screen so a form update cannot rewrite the year. */
  const [pendingInput, setPendingInput] = useState<string | null>(null);
  const inputValue = pendingInput !== null ? pendingInput : naturalDateInputValue;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatNaturalDateInput(e.target.value);
      setPendingInput(formatted);
      setFieldValue(id, parseNaturalDateInput(formatted));
      updateFloatingLabelState({ isFilled: !!formatted, hasFocus: true });
    },
    [id, setFieldValue, updateFloatingLabelState]
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const formatted = formatNaturalDateInput(e.currentTarget.value);
      setPendingInput(null);
      setFieldTouched(`${id}.month`, true);
      setFieldTouched(`${id}.day`, true);
      setFieldTouched(`${id}.year`, true);
      const parsed = parseNaturalDateInput(formatted);
      setFieldValue(id, parsed);
      updateFloatingLabelState({ isFilled: isDateFilled(parsed), hasFocus: false });
    },
    [id, setFieldTouched, setFieldValue, updateFloatingLabelState]
  );

  const handleFocus = useCallback(() => {
    setPendingInput(naturalDateInputValue);
    updateFloatingLabelState({ isFilled: true, hasFocus: true });
  }, [naturalDateInputValue, updateFloatingLabelState]);

  const inputId = `date-input-${id.replace(/[.[\]]/g, '-')}`;
  const showOverlay = !floatingLabelState.isFilled && !floatingLabelState.hasFocus;

  return (
    <>
      <FloatingLabelWrapper
        className={`date-input-single ${!_.isEmpty(error) ? 'invalid' : ''}`}
        state={floatingLabelState}
        disabled={disabled}
      >
        {showOverlay && (
          <label className="flabel-title" htmlFor={inputId}>
            <Text
              id={defaultLabelId ? `defaults.${defaultLabelId}` : id}
              textKey="label"
              type="field"
            />
            {' (MM/DD/YYYY)'}
          </label>
        )}
        <div className="flabel-value">
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            autoComplete={isDateOfBirth ? 'bday' : 'off'}
            className="flabel-input"
            value={inputValue}
            maxLength={10}
            placeholder={floatingLabelState.hasFocus ? 'MM/DD/YYYY' : ''}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            disabled={disabled}
            aria-invalid={!_.isEmpty(error)}
            aria-describedby={!_.isEmpty(error) && showError ? `${inputId}-error` : undefined}
          />
        </div>
      </FloatingLabelWrapper>
      {!_.isEmpty(error) && showError && (
        <span id={`${inputId}-error`} className="error">
          <Text id={id} textKey={'error'} type={'error'} />
        </span>
      )}
    </>
  );
};
